import React from 'react';
import { Box, Button, SvgIcon } from '@mui/material';
import { ReactComponent as GoogleIcon } from 'assets/images/Google__G__Logo.svg';
import { MUSEFLOW_OFF_WHITE_1 } from 'Components/Colors';
import LastUsedBadge from 'Components/LastUsedBadge';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import { getLastLoginMethod, LastLoginMethod } from 'Utils/LastLoginMethod';
import { authColumnWidth } from './authColumn';

type OidcProvider = 'Google' | 'SignInWithApple';

type SocialProvider = {
  provider: OidcProvider;
  lastLoginMethod: LastLoginMethod;
  label: string;
  icon: React.ReactNode;
};

const AppleIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const SOCIAL_PROVIDERS: readonly SocialProvider[] = [
  {
    provider: 'Google',
    lastLoginMethod: 'google',
    label: 'Google',
    icon: <GoogleIcon />,
  },
  {
    provider: 'SignInWithApple',
    lastLoginMethod: 'apple',
    label: 'Apple',
    icon: <AppleIcon />,
  },
];

const captionStyles = {
  textAlign: 'center',
  color: MUSEFLOW_OFF_WHITE_1,
  paddingBottom: '8px',
  fontFamily: 'Lato',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: 'normal',
  letterSpacing: '0.16px',
};

const rowStyles = {
  // Pin the method row to the form's framing width and split it into two equal
  // halves. The combined width is therefore fixed: when the "Last used" tag
  // appears inside a button, that half can't grow, so its padding self-trims by
  // exactly the tag width - the pair never widens past the framing and stays
  // symmetric.
  width: authColumnWidth,
  maxWidth: authColumnWidth,
  display: 'flex',
  gap: '6px',
  alignItems: 'center',
};

const buttonStyles = {
  flex: 1,
  minWidth: 0,
  fontSize: '16px',
  color: MUSEFLOW_OFF_WHITE_1,
  borderStyle: 'solid',
  borderColor: MUSEFLOW_OFF_WHITE_1,
  borderRadius: '40px',
  borderWidth: '2px',
  padding: '8px 12px',
  whiteSpace: 'nowrap',
};

/**
 * "Continue with:", then the Google and Apple buttons side by side: the top of
 * both the sign-in and the sign-up page. Either button signs the user in,
 * creating the account first when there is none, so the same row serves both
 * pages. The button for the method last used on this device carries a "Last
 * used" tag.
 */
const SocialSignInButtons: React.FC = () => {
  const { logInOidc } = useAuthWrapperContext();
  const captionId = React.useId();

  // Which method this device last signed in with (null = clean first run).
  // Read once on mount; a successful sign-in navigates away and re-reads on return.
  const lastLoginMethod = React.useMemo(() => getLastLoginMethod(), []);

  return (
    <Box role="group" aria-labelledby={captionId}>
      <Box id={captionId} sx={captionStyles}>
        Continue with:
      </Box>
      <Box sx={rowStyles}>
        {SOCIAL_PROVIDERS.map((socialProvider) => (
          <Button
            key={socialProvider.provider}
            sx={buttonStyles}
            onClick={async () => {
              await logInOidc(socialProvider.provider);
            }}
            startIcon={<SvgIcon>{socialProvider.icon}</SvgIcon>}
          >
            {socialProvider.label}
            {lastLoginMethod === socialProvider.lastLoginMethod && (
              <LastUsedBadge variant="inline" />
            )}
          </Button>
        ))}
      </Box>
    </Box>
  );
};

export default SocialSignInButtons;
